import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import styles from './styles.module.css'
import AccessoryImageWithTooltip from './AccessoryImageWithTooltip'
import { useUiStore } from '../../../../store/uiStore'
import { useCartStore, selectCart } from '../../../../store/cartStore'
import { useAddToCart } from '../../../hooks/useAddToCart'
import useProductOffers from '../../../../hooks/useProductOffers'
import { $neutralGray70 } from '../../../../styles/colors/primitives'
import { CircularProgress } from '@mui/material'
import Text from '../../../Text'
import Subtotal from '../ProductCheckoutPage/Subtotal'
import {
  calculatePriceForMultipleShopItems,
  calculateDiscountFromRegularPriceCents,
  findBundleByBundledItemTypes,
  getShopItemPrice,
  getShopItemOriginalPrice,
} from '../../../../utils/shopItemsAndCart'
import SecondaryNavbarAddToCart from '../ProductCheckoutPage/SecondaryNavbarAddToCart'
import CartIcon from '../../../icons/CartIcon'
import ProductInfoTags, { RIG_TRANSIT_TIME_DAYS } from '../ProductCheckoutPage/ProductInfoTags'
import Carousel from '../ProductCheckoutPage/Carousel'
import { findItemByType, isOutOfStock, getOutOfStockMessage, isUnavailableInRegion } from '../../../../utils/shopItems'
import CheckmarkIcon from '../../../icons/CheckmarkIcon'
import Description from '../ProductCheckoutPage/Description'
import KeyFeatures from './KeyFeatures'
import ProductGallery from '../Utils/ProductGallery'
import Gt2ShopMarketing, { Gt2Pedigree } from './Gt2ShopMarketing'
import Gt2FeatureRows from './Gt2FeatureRows'
import Gt2VideoBand from './Gt2VideoBand'
import Gt2Compatibility from './Gt2Compatibility'
import Gt2SpecsTabs from './Gt2SpecsTabs'
import Gt2Close from './Gt2Close'
import Gt2TrustRow from './Gt2TrustRow'
import {
  Gt2SpecStrip,
  Gt2PriceLine,
  Gt2Promise,
  Gt2Group,
  Gt2MountCards,
} from './Gt2BuyPanel'
import SimpleBlockContent from '../../../SimpleBlockContent'
import SelectableItem from '../Utils/SelectableItem'
import { get } from '../../../../utils/http'
import { getSelectedItems as getSelectedItemsUtil } from './utils'
import { DEFAULT_MOUNT, resolveMountOptions } from '../../../../lib/gt2Mount'
import { addCentsToPrice } from '../../../../utils/prices'
import { formatPrice } from '../../../../utils/formats'

const cx = (...c) => c.filter(Boolean).join(' ')

// Free-standing monitor stands listed under the chassis-mounted ones in the
// monitor stands step, keyed by catalog productSetId.
const FREE_STANDING_STANDS = [
  { id: 'free-standing-tx43', productSetId: 'triple-monitor-stand-tx43', label: 'Triple Monitor Stand (TX43)' },
  { id: 'free-standing-tx60', productSetId: 'triple-tv-stand-tx60', label: 'Triple TV Stand (TX60)' },
]

// Split the cockpit description around the "Key Features" section. The Key
// Features bullets are rendered always-visible (green-check checklist) instead
// of being buried in the collapsible <Description>; everything from the next
// section heading onward (Materials, Dimensions, ...) stays collapsible. Falls
// back to a single collapsible Description if no "Key Features" heading exists.
function splitCockpitDescription(blocks) {
  if (!Array.isArray(blocks)) return { intro: [], keyFeatures: [], rest: blocks }
  const isKeyFeaturesHeading = (b) =>
    b && b.style === 'heading-6' &&
    (b.children || []).map((c) => c.text || '').join('').trim().toLowerCase() === 'key features'
  const kf = blocks.findIndex(isKeyFeaturesHeading)
  if (kf === -1) return { intro: [], keyFeatures: [], rest: blocks }
  let end = kf + 1
  while (end < blocks.length && blocks[end].listItem === 'bullet') end++
  return {
    intro: blocks.slice(0, kf),
    keyFeatures: blocks.slice(kf + 1, end),
    rest: blocks.slice(end),
  }
}

// `h1` (optional, from the CMS block) overrides ONLY the visible page heading,
// so the H1 can carry the search phrase people actually type ("sim racing
// cockpit") while `title` stays the short product name used by the sticky
// add-to-cart bar, the CTA labels and breadcrumbs.
function GT2CockpitCheckoutPage({ title, h1, description, importantInfo }) {
  // Cart line items now come from the Zustand cart store (Bucket C); the cart panel
  // flag from the Zustand ui store (Phase C2 part 1).
  const shoppingCart = useCartStore(selectCart)
  const openCart = useUiStore((s) => s.openCart)
  const { checkoutPageAddToCart } = useAddToCart()

  // Shared product-checkout data logic (task #95). GT2 is the outlier of the four
  // forks: its gallery comes from the /api/getConfigurationImages endpoint keyed
  // on the color/seat configuration, not from getOfferImages/getItemOffers — so it
  // consumes useProductOffers ONLY for the client-side full-offers refetch gate
  // (the one piece all four share). No itemType is passed: GT2 derives the cockpit
  // item from the selected color, not a single fixed itemType. `offers` (the
  // server-threaded / full-after-refetch offers) replaces the render-time
  // `state.shopItems` reads below (byte-identical value throughout); cart reads
  // (state.shoppingCart) stay on Overmind (Bucket C).
  const { offers: shopItems, shopItemsFetched } = useProductOffers()

  // Get available colors from shop items (GT2 cockpits with colorInfo)
  const availableColors = useMemo(() => (shopItems || [])
    .filter(item => item.isGT2Cockpit && item.colorInfo)
    .map(item => ({
      colorName: item.colorInfo.colorName,
      colorHex: item.colorInfo.colorHex,
      itemType: item.type,
      isOutOfStock: isOutOfStock(item)
    })), [shopItems])

  // Wheelbase mounting plate. Empty until the backend serves the kit offers, in
  // which case the control is not rendered at all (see lib/gt2Mount).
  const mountOptions = useMemo(() => resolveMountOptions(shopItems), [shopItems])

  // Configuration state - default to first available color
  const [selectedColor, setSelectedColor] = useState(null)
  const [selectedMount, setSelectedMount] = useState(DEFAULT_MOUNT)
  const [includeSeat, setIncludeSeat] = useState(true)
  const [selectedAccessories, setSelectedAccessories] = useState({}) // { accessoryId: true/false }
  const [selectedVesaVariants, setSelectedVesaVariants] = useState({}) // { groupId: itemId }
  const [activeStep, setActiveStep] = useState(1)
  const [configurationImages, setConfigurationImages] = useState([])
  const imagesCacheRef = useRef({}) // Cache for configuration images by itemTypes key

  // Set default color when colors become available (prefer in-stock colors)
  useEffect(() => {
    if (availableColors.length > 0 && !selectedColor) {
      const inStockColor = availableColors.find(c => !c.isOutOfStock)
      setSelectedColor(inStockColor?.colorName || availableColors[0].colorName)
    }
  }, [availableColors, selectedColor])

  // Keep the mount selection on an option this region actually offers — e.g. the
  // default front kit going out of stock, which would otherwise leave
  // `selectedMount` pointing at nothing and drop the kit line silently.
  useEffect(() => {
    if (mountOptions.length > 0 && !mountOptions.some((m) => m.id === selectedMount)) {
      setSelectedMount(mountOptions[0].id)
    }
  }, [mountOptions, selectedMount])

  const selectedMountOption = mountOptions.find((m) => m.id === selectedMount) || null
  const mountKitItem = selectedMountOption?.item || null

  // Get seat item dynamically from shop items. Exclude the cockpit+seat bundle,
  // which also carries isGT2Seat=true — we want the standalone GT-2 Seat here.
  // (The full-offers refetch is handled by useProductOffers() above, #95.)
  const seatItem = (shopItems || []).find(item => item.isGT2Seat && !item.isGT2Cockpit)

  // Group monitor stands by type with VESA options
  const monitorStandGroups = useMemo(() => {
    const groups = []
    // isGT2Accessory gates the list: only mounts that bolt onto the GT-2 chassis
    // belong in the configurator. The free-standing TX43/TX60 stands carry the
    // triple-mount flag (they want the VESA dropdown on their own product page)
    // but are sold standalone, so they set isGT2Accessory=false and drop out here.
    const singleMounts = (shopItems || []).filter(item => item.isGT2SingleMonitorMount && item.isGT2Accessory)
    const tripleMounts = (shopItems || []).filter(item => item.isGT2TripleMonitorMount && item.isGT2Accessory)

    if (singleMounts.length > 0) {
      groups.push({
        id: 'single-monitor',
        label: 'Single Monitor Mount',
        variants: singleMounts,
        defaultVariant: singleMounts[0]
      })
    }

    if (tripleMounts.length > 0) {
      groups.push({
        id: 'triple-monitor',
        label: 'Triple Monitor Mount',
        variants: tripleMounts,
        defaultVariant: tripleMounts[0]
      })
    }

    // Free-standing stands (TX43 triple monitor, TX60 triple TV). They do not
    // bolt to the chassis, so their catalog docs keep isGT2Accessory=false, but
    // they are offered here too as the stand-alone alternative. Picked by
    // product family; a family with no price in this region is left out.
    FREE_STANDING_STANDS.forEach(({ id, productSetId, label }) => {
      const variants = (shopItems || [])
        .filter(item => item.productSetId === productSetId && !isUnavailableInRegion(item))
        .sort((a, b) => String(a.type).localeCompare(String(b.type)))
      if (variants.length > 0) {
        groups.push({ id, label, variants, defaultVariant: variants[0], freeStanding: true })
      }
    })

    return groups
  }, [shopItems])

  const otherAccessories = useMemo(() => (shopItems || [])
    .filter(item => item.isGT2Accessory && !item.isGT2SingleMonitorMount && !item.isGT2TripleMonitorMount)
    , [shopItems])

  // Fetch configuration images when color or seat selection changes
  const fetchConfigurationImages = useCallback(async () => {
    const colorConfig = availableColors.find(c => c.colorName === selectedColor)
    if (!colorConfig) return

    const itemTypes = [colorConfig.itemType]
    if (includeSeat && seatItem?.type) {
      itemTypes.push(seatItem.type)
    }

    // Create cache key from sorted item types + the mount, which selects a
    // different render set for the same colour/seat combination.
    const cacheKey = `${itemTypes.sort().join(',')}|${selectedMount}`

    // Check cache first
    if (imagesCacheRef.current[cacheKey]) {
      setConfigurationImages(imagesCacheRef.current[cacheKey])
      return
    }

    try {
      const response = await get(`/api/getConfigurationImages?itemTypes=${itemTypes.join(',')}&mount=${selectedMount}`)
      if (response.data && Array.isArray(response.data)) {
        // Store in cache
        imagesCacheRef.current[cacheKey] = response.data
        setConfigurationImages(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch configuration images:', error)
      // Fallback to cockpit gallery images
      const cockpitItem = findItemByType(colorConfig.itemType, shopItems)
      if (cockpitItem?.galleryImages) {
        setConfigurationImages(cockpitItem.galleryImages)
      }
    }
  }, [selectedColor, selectedMount, includeSeat, availableColors, seatItem, shopItems])

  useEffect(() => {
    if (selectedColor) {
      fetchConfigurationImages()
    }
  }, [selectedColor, selectedMount, includeSeat, fetchConfigurationImages])

  // Get cockpit item for the selected color
  const getCockpitItem = () => {
    const colorConfig = availableColors.find(c => c.colorName === selectedColor)
    if (!colorConfig) return null
    return findItemByType(colorConfig.itemType, shopItems)
  }

  // Find cockpit + seat bundle if it exists
  const getCockpitSeatBundle = () => {
    const cockpitItem = getCockpitItem()
    if (!cockpitItem || !seatItem || !includeSeat) return null

    const bundledTypes = [cockpitItem.type, seatItem.type]
    return findBundleByBundledItemTypes(shopItems, bundledTypes)
  }

  // Get all selected items for cart (uses bundle when cockpit + seat selected)
  const getSelectedItems = () => {
    const cockpitItem = getCockpitItem()
    const bundle = getCockpitSeatBundle()

    // If bundle exists and is in stock, use it instead of individual items
    if (bundle && !isOutOfStock(bundle)) {
      const items = [bundle]

      // The mount kit is a chassis property, so it rides with the cockpit+seat
      // bundle exactly as it does with the standalone cockpit.
      if (mountKitItem && !isOutOfStock(mountKitItem)) {
        items.push(mountKitItem)
      }

      // Add selected accessories
      monitorStandGroups.forEach(group => {
        if (selectedAccessories[group.id]) {
          const selectedVariantId = selectedVesaVariants[group.id]
          const variant = group.variants.find(v => v.id === selectedVariantId) || group.defaultVariant
          if (variant && !isOutOfStock(variant)) {
            items.push(variant)
          }
        }
      })

      otherAccessories.forEach(acc => {
        if (selectedAccessories[acc.id] && !isOutOfStock(acc)) {
          items.push(acc)
        }
      })

      return items
    }

    // No bundle available, use individual items
    return getSelectedItemsUtil({
      cockpitItem,
      mountKitItem,
      includeSeat,
      seatItem,
      monitorStandGroups,
      selectedAccessories,
      selectedVesaVariants,
      otherAccessories
    })
  }

  // Check if items are in cart
  const isInCart = () => {
    const selectedItems = getSelectedItems()
    return selectedItems.length > 0 && selectedItems.every(item =>
      shoppingCart[item.id]
    )
  }

  // Toggle accessory selection
  const toggleAccessory = (accessoryId) => {
    setSelectedAccessories(prev => ({
      ...prev,
      [accessoryId]: !prev[accessoryId]
    }))
  }

  // Update VESA variant for a monitor stand group
  const updateVesaVariant = (groupId, itemId) => {
    setSelectedVesaVariants(prev => ({
      ...prev,
      [groupId]: itemId
    }))
  }

  // Extract VESA size from item name or type (e.g., "100x100" or "200x200")
  const getVesaSize = (item) => {
    const match = item.type?.match(/(\d+x\d+)$/) || item.name?.match(/(\d+x\d+)/)
    return match ? match[1] : null
  }

  // Step numbering + the "you are here" highlight. A step that is not offered
  // (no mount kits in this region, no seat, ...) is skipped, so the numbers stay
  // 1, 2, 3 with no gaps.
  const stepProps = (step) => ({
    step,
    active: activeStep === step,
    onActivate: () => setActiveStep(step),
  })

  // Render monitor stand groups with VESA dropdown
  const renderMonitorStandSection = (step) => {
    if (monitorStandGroups.length === 0) return null

    return (
      <Gt2Group {...stepProps(step)} label="Monitor mounts & stands">
        <div className={styles.accessoriesWrapper}>
          {monitorStandGroups.map((group, i) => {
            // Section labels: "Rig-mounted" over the chassis mounts, "Free-standing"
            // over the stand-alone stands, each shown on the first of its kind.
            const subgroupLabel = i === 0 || !!group.freeStanding !== !!monitorStandGroups[i - 1].freeStanding
              ? (group.freeStanding ? 'Free-standing' : 'Rig-mounted')
              : null
            const selectedVariantId = selectedVesaVariants[group.id]
            const selectedVariant = group.variants.find(v => v.id === selectedVariantId) || group.defaultVariant
            const outOfStock = selectedVariant ? isOutOfStock(selectedVariant) : true

            return (
              <React.Fragment key={group.id}>
                {subgroupLabel && <span className={styles.subgroupLabel}>{subgroupLabel}</span>}
                <SelectableItem
                  selected={selectedAccessories[group.id]}
                  disabled={outOfStock}
                  disabledText={selectedVariant ? getOutOfStockMessage(selectedVariant) : 'Out of stock'}
                  onClick={() => toggleAccessory(group.id)}
                  image={<AccessoryImageWithTooltip src={selectedVariant?.imageUrl} alt={group.label} galleryImages={selectedVariant?.galleryImages}/>}
                  name={group.label}
                  price={selectedVariant ? getShopItemPrice(selectedVariant) : undefined}
                  regularPrice={selectedVariant ? getShopItemOriginalPrice(selectedVariant) : undefined}
                >
                  {!outOfStock && group.variants.length > 1 && (
                    <select
                      className={styles.vesaSelect}
                      value={selectedVariant?.id || ''}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateVesaVariant(group.id, e.target.value)}
                    >
                      {group.variants.map(variant => (
                        <option key={variant.id} value={variant.id}>
                          VESA {getVesaSize(variant)}
                        </option>
                      ))}
                    </select>
                  )}
                </SelectableItem>
              </React.Fragment>
            )
          })}
        </div>
      </Gt2Group>
    )
  }

  // Render an accessory section
  const renderAccessorySection = (step, sectionTitle, items) => {
    if (items.length === 0) return null

    return (
      <Gt2Group {...stepProps(step)} label={sectionTitle}>
        <div className={styles.accessoriesWrapper}>
          {items.map(acc => (
            <SelectableItem
              key={acc.id}
              selected={selectedAccessories[acc.id]}
              disabled={isOutOfStock(acc)}
              disabledText={getOutOfStockMessage(acc)}
              onClick={() => toggleAccessory(acc.id)}
              image={<AccessoryImageWithTooltip src={acc.imageUrl} alt={acc.name} galleryImages={acc.galleryImages}/>}
              name={acc.name}
              price={getShopItemPrice(acc)}
              regularPrice={getShopItemOriginalPrice(acc)}
            />
          ))}
        </div>
      </Gt2Group>
    )
  }

  // Render loading state
  if (!shopItemsFetched || !selectedColor) {
    return (
      <div className={styles.loading} style={{ color: $neutralGray70 }}>
        <CircularProgress color="inherit"/>
      </div>
    )
  }

  const cockpitItem = getCockpitItem()

  // Check the cockpit exists AND is purchasable in the resolved region. On /us or
  // /au the GT-2 cockpit colours are still present in the catalog join (colorInfo
  // comes from the catalog, so availableColors is non-empty and a colour gets
  // pre-selected) but carry no price — an EU-only rig (#46/#137). Guard on
  // isUnavailableInRegion too, or the configurator would render a $0 subtotal /
  // dead buy box instead of the unavailable message. SSR-deterministic (price +
  // availability are fixed server-side).
  if (!cockpitItem || isUnavailableInRegion(cockpitItem)) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.contentWrapper}>
          <Text type="heading-4">Product not available in your region.</Text>
        </div>
      </div>
    )
  }

  // Use configuration images from API, fallback to cockpit gallery images
  const images = configurationImages.length > 0 ? configurationImages : (cockpitItem.galleryImages || [])

  // Split the description ONCE. The buy box keeps the intro + Key Features (through
  // "100% made in the EU"); everything after it (Materials, Dimensions, What's
  // Included, ...) moves into the specs tabs below the marketing content.
  const { intro, keyFeatures, rest } = splitCockpitDescription(description || [])

  // "Optional" tab = the add-ons actually offered in this configurator, so the
  // list can never drift from what is really purchasable.
  const optionalItems = [
    ...(seatItem?.name ? [seatItem.name] : []),
    ...monitorStandGroups.map((g) => g.label),
    ...otherAccessories.map((a) => a?.name).filter(Boolean),
  ].filter((v, i, arr) => v && arr.indexOf(v) === i)
  // Configurator steps in page order, numbered over the ones actually shown.
  const steps = {}
  ;[
    ['colour', availableColors.length > 0],
    ['mount', mountOptions.length > 1],
    ['seat', !!seatItem],
    ['monitor', monitorStandGroups.length > 0],
    ['extras', otherAccessories.length > 0],
  ].filter(([, shown]) => shown).forEach(([key], i) => { steps[key] = i + 1 })

  const selectedItems = getSelectedItems()
  const subtotal = calculatePriceForMultipleShopItems(selectedItems)
  const discountCents = calculateDiscountFromRegularPriceCents(selectedItems)
  const subtotalBeforeDiscount = addCentsToPrice(subtotal, discountCents)
  const itemsInCart = isInCart()

  const onAddToCartClick = () => {
    checkoutPageAddToCart(selectedItems.map(i => i.id))
  }

  const getAddToCartCta = (isMainCta) => ({
    title: itemsInCart ? 'Item added' : 'Add to cart',
    titleIcon: itemsInCart ? <CheckmarkIcon/> : (isMainCta ? <CartIcon large/> : <CartIcon/>),
    light: itemsInCart,
    sx: {
      height: isMainCta ? '80px' : null,
      titleTextType: isMainCta ? 'large' : null,
      width: isMainCta ? '100%' : null,
    },
    disabled: selectedItems.length === 0,
    onClick: () => {
      if (itemsInCart) {
        openCart()
      } else {
        onAddToCartClick()
      }
    }
  })

  return (
    <>
      {/* Product JSON-LD is emitted server-side from the page render path
          (components/ProductStructuredData via [[...slug]].js), region-correct
          and de-duplicated. Do not re-add a per-section block here. */}
      <SecondaryNavbarAddToCart
        title={title || 'GT-2 Cockpit'}
        subtotal={subtotal}
        subtotalBeforeDiscount={subtotalBeforeDiscount}
        ctaV2={getAddToCartCta(false)}
      />
      <div className={styles.wrapper}>
        <div className={styles.contentWrapper}>
          <div className={styles.leftColumn}>
            <div className={styles.desktopCarousel}>
              <ProductGallery images={images} resetKey={`${selectedColor}_${includeSeat}_${selectedMount}`}/>
            </div>
            {/* Marketing rides in the LEFT column, directly under the gallery, so
                expanding "Continue reading" in the buy box grows only the RIGHT
                column and never pushes this content down. On mobile the whole
                left column is ordered after the buy box (see styles). */}
            <Gt2ShopMarketing/>
          </div>
          <div className={styles.productInfo}>
            <div className={styles.productText}>
              <div className={styles.productTitle}>
                <Text type="heading-10">VRS® DirectForce®</Text>
                <Text type="heading-3" primary>{h1 || title || 'GT-2 Cockpit'}</Text>
              </div>
              {/* Price sits with the name. It used to appear only in the sticky
                  bar and in the subtotal at the very bottom of the configurator.
                  It reads live off the same `subtotal` the CTA charges, so the
                  two can never disagree. */}
              {/* Guarded: formatPrice returns the string 'N/A' for a missing
                  price, which is truthy, so the row would render "N/A" beside
                  the H1 during an offers refetch. Pass null instead and the
                  row hides itself until a real subtotal exists. */}
              <Gt2PriceLine
                price={subtotal ? formatPrice(subtotal) : null}
                note="as configured, VAT included"
              />
              <Gt2Promise>
                A professional sim racing cockpit engineered to eliminate flex, so the only
                thing you feel through the wheel is the car.
              </Gt2Promise>
              {/* Four numbers instead of nine red-check bullets. The old
                  KeyFeatures list repeated the trust row, the proof band under
                  the gallery AND the ProductInfoTags accordion. */}
              <Gt2SpecStrip/>
              {importantInfo && <SimpleBlockContent blocks={importantInfo}/>}
            </div>
            <div className={styles.mobileCarousel}><Carousel key={`${selectedColor}_${selectedMount}`} images={images}/></div>
            <div className={styles.checkout}>
              {/* Color Selection */}
              {availableColors.length > 0 && (() => {
                const selectedColorConfig = availableColors.find(c => c.colorName === selectedColor)
                const selectedColorOutOfStock = selectedColorConfig?.isOutOfStock
                return (
                  <Gt2Group
                    {...stepProps(steps.colour)}
                    label="Colour"
                    value={`${selectedColor}${selectedColorOutOfStock ? ' (out of stock)' : ''}`}
                  >
                    <div className={styles.colorSwatchesWrapper}>
                      {availableColors.map(color => (
                        <button
                          key={color.colorName}
                          className={cx(
                            styles.colorSwatch,
                            selectedColor === color.colorName && styles.colorSwatchSelected,
                            color.isOutOfStock && styles.colorSwatchDisabled
                          )}
                          style={{ '--swatch-color': color.colorHex }}
                          onClick={() => !color.isOutOfStock && setSelectedColor(color.colorName)}
                          aria-label={color.colorName}
                          disabled={color.isOutOfStock}
                        />
                      ))}
                    </div>
                  </Gt2Group>
                )
              })()}

              {/* Wheelbase mount. Same price either way — it picks the mounting
                  plate that ships with the chassis, and adds a zero-cost kit
                  line so the order says which one to pack. */}
              {mountOptions.length > 1 && (
                <Gt2Group
                  {...stepProps(steps.mount)}
                  label="Wheelbase mount"
                  value={(mountOptions.find((o) => o.id === selectedMount) || {}).label}
                >
                  {/* Two cards, not a <select>. This is the one choice on the
                      page a buyer can get wrong, and a dropdown hid the
                      difference behind a click. */}
                  <Gt2MountCards
                    options={mountOptions}
                    value={selectedMount}
                    onChange={setSelectedMount}
                  />
                </Gt2Group>
              )}

              {/* Seat Toggle */}
              {seatItem && (
                <Gt2Group {...stepProps(steps.seat)} label="Seat" value={includeSeat ? 'Included' : 'No seat'}>
                  <div className={styles.accessoriesWrapper}>
                    <SelectableItem
                      selected={includeSeat}
                      disabled={isOutOfStock(seatItem)}
                      onClick={() => setIncludeSeat(!includeSeat)}
                      image={<AccessoryImageWithTooltip src={seatItem.imageUrl} alt={seatItem.name} galleryImages={seatItem.galleryImages}/>}
                      name={seatItem.name}
                      price={getShopItemPrice(seatItem)}
                      regularPrice={getShopItemOriginalPrice(seatItem)}
                    />
                  </div>
                </Gt2Group>
              )}

              {/* Monitor Stands */}
              {renderMonitorStandSection(steps.monitor)}

              {/* Other Accessories. Titled "Complete your cockpit" rather than
                  the flat "Accessories" so the block reads as a finish-the-build
                  prompt instead of an inventory list. */}
              {renderAccessorySection(steps.extras, 'Complete your cockpit', otherAccessories)}

              {/* Reassurance (money back / delivery / warranty / secure payment)
                  sits ABOVE the price + Add to cart, not under it: buyers look
                  for it right before committing, and below the CTA it was below
                  the fold on most viewports. */}
              {/* The four facts that used to sit here (EU build, 95% pre-assembled,
                  3-year warranty, 30-day money back) are now rows in the key-features
                  table under the price, so this would be the third place on the page
                  saying them. Gt2TrustLine is still exported if you want it back. */}

              {/* Subtotal */}
              <div className={styles.subtotalAndPreOrder}>
                <Subtotal
                  subtotal={subtotal}
                  discountCents={discountCents}
                  taxIncluded={cockpitItem?.taxIncluded}
                  cta={getAddToCartCta(true)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Full-width bands under the two-column area, ordered the way a buyer
          actually decides: the build story (FeatureRows) -> who races it
          (Pedigree) -> does my gear fit (Compatibility) -> the deep numbers
          (SpecsTabs) -> the reason to buy once (Close). The deep spec text lives
          in SpecsTabs rather than behind the buy box's "Continue reading". */}
      {/* Edge-to-edge b-roll strip opening the build story. */}
      <Gt2VideoBand/>
      <Gt2FeatureRows/>
      <Gt2Pedigree/>
      <Gt2Compatibility/>
      <Gt2SpecsTabs blocks={rest} optionalItems={optionalItems}/>
      <Gt2Close/>
      {/* Fine print: the rig is sold as a chassis; wheel bases, pedals, wheels
          and any other hardware shown in imagery are not included. */}
      <p className={styles.disclaimer}>
        Any hardware not explicitly listed above is not included with the rig. Wheel bases, pedals, steering wheels, monitors and other equipment shown in product imagery are sold separately.
      </p>
    </>
  )
}

export default GT2CockpitCheckoutPage
